import { Popover } from "react-tiny-popover";
import "./NfsDropdown.css";
import { ReactNode, useEffect, useRef, useState } from "react";
import ArrowDownIcon from "../../../assets/arrow-down.svg?react";
import DoneIcon from "../../../assets/done.svg?react";
import CancelIcon from "../../../assets/cancel.svg?react";
import Loader from "../Loader/Loader";

interface NfsDropdownProps {
    options: string[];
    selectedOption: string | null;
    setSelectedOption: (option: string | null) => Promise<boolean | undefined> | void;
    placeholder: string;
    allowEmpty?: boolean;
    displayValueFunction?: (option: number | string) => any;
    displayClassName?: string;
    iconReplacement?: ReactNode;
    disabled?: boolean;
    isSaving?: boolean;
    readonly?: boolean;
    searchable?: boolean;
}

const NfsDropdown = (props: NfsDropdownProps) => {
    const [isOpen, setIsOpen] = useState(false);

    const [searchValue, setSearchValue] = useState("");
    const [filteredOptions, setFilteredOptions] = useState<string[]>(props.options);

    const searchFieldRef = useRef<HTMLInputElement>(null);

    const toggleIsOpen = () => {
        if (props.disabled || props.readonly) {
            setIsOpen(false);
            return;
        }
        setIsOpen(!isOpen);
    };

    const [savedState, setSavedState] = useState<boolean | undefined>(undefined);

    const showSavedStateForInterval = (state: boolean | undefined) => {
        setSavedState(state);
        if (state === undefined) return;
        setTimeout(() => {
            setSavedState(undefined);
        }, 5000);
    };


    useEffect(() => {
        if (props.searchable) {
            if (isOpen) {
                setTimeout(() => {
                    searchFieldRef.current?.focus({ preventScroll: true });
                }, 100);
            }
        }
    }, [props.searchable, isOpen]);

    useEffect(() => {
        if (searchValue === "") {
            setFilteredOptions(props.options);
            return;
        }
        const filtered = props.options.filter((option) => option.toLowerCase().includes(searchValue.toLowerCase()));
        setFilteredOptions(filtered);
    }, [searchValue, props.options]);

    return (
        <Popover
            isOpen={isOpen}
            onClickOutside={() => setIsOpen(false)}
            positions={["bottom", "top"]}
            align="start"
            containerClassName="nfs-dropdown-panel"
            content={
                <>
                    {props.searchable && (
                        <input
                            autoFocus
                            ref={searchFieldRef}
                            type="text"
                            className="nfs-dropdown-search"
                            value={searchValue}
                            placeholder="Søk..."
                            onChange={(e) => {
                                setSearchValue(e.target.value);
                            }}
                        />
                    )}
                    <div className="nfs-dropdown-panel">
                        {props.allowEmpty && props.selectedOption !== null && (
                            <button
                                className="nfs-dropdown-option"
                                onClick={() => {
                                    props.setSelectedOption(null);
                                    setIsOpen(false);
                                }}
                            >
                                {props.placeholder}
                            </button>
                        )}
                        {filteredOptions.map((option) => {
                            if (option === props.selectedOption) {
                                return null;
                            }
                            return (
                                <button
                                    key={option}
                                    className="nfs-dropdown-option"
                                    onClick={() => {
                                        props.setSelectedOption(option)?.then((result) => showSavedStateForInterval(result));
                                        setIsOpen(false);
                                    }}
                                >
                                    {!props.displayValueFunction && option}
                                    {props.displayValueFunction?.(option)}
                                </button>
                            );
                        })}
                    </div>
                </>
            }
        >
            <button
                className={`nfs-dropdown ${props.displayClassName ?? ""}`}
                disabled={props.disabled}
                onClick={toggleIsOpen}
            >
                <span className="nfs-dropdown-text">
                    {props.selectedOption !== null ? (
                        <>
                            {!props.displayValueFunction && props.selectedOption}
                            {props.displayValueFunction?.(props.selectedOption)}
                        </>
                    ) : (
                        props.placeholder
                    )}
                </span>
                {props.isSaving ? (
                    <Loader size={16} inline />
                ) : (
                    savedState !== undefined &&
                    <div className="center" style={{ height: "20px", width: "20px", backgroundColor: savedState ? "green" : "red", borderRadius: "25%" }}>{savedState ? <DoneIcon color="white" /> : <CancelIcon color="white" />}</div>
                )}
                {props.iconReplacement ? (
                    props.iconReplacement
                ) : (
                    props.options.length > 1 && !props.readonly && <ArrowDownIcon style={{ flexShrink: "0" }} />
                )}
            </button>
        </Popover>
    );
};

export default NfsDropdown;
